@use "@styles/_variables/colors" as *;
@use "@styles/_variables/breakpoints" as *;
@use "@styles/_variables/sizes" as *;
@use "@styles/_variables/mixins" as *;
@use "@styles/_variables/typography" as *;

.information {
  position: relative;
  margin-top: 0;
  margin-bottom: $tab-double;

  // Eyebrow above the section title. It shares the h2's left edge: the icon
  // gutter is exactly $header-logo-size + $header-logo-spacing wide, and the
  // card below uses the same margin — kicker, title and card hang off one line.
  .kicker {
    @include kicker();
    margin-left: $header-logo-size + $header-logo-spacing;
    margin-bottom: 1rem;
  }

  .column {
    display: flex;
    flex-direction: row;

    .header {
      display: flex;
      position: relative;
      margin-bottom: $tab;
      align-items: center;

      h2 {
        font-family: $font-display-bold;
        font-size: $section-title-font-size;
        color: $white;
        @include sciFiTitleGlow();
      }

      .icon-wrapper {
        --reach: 2.3rem; // scanline travel: half the reticle box
        display: flex;
        // The wrapper is exactly the icon box, pushed against the title so
        // the icon sits $tab from the text — the same token as the header's
        // margin-bottom, so the icon-to-text gap always equals the
        // text-to-card gap below. Margins + width still sum to the full
        // logo gutter ($header-logo-size + $header-logo-spacing): the h2,
        // and the card aligned under it, never move. Wrapper == icon box
        // also keeps the reticle pseudos (centered on the wrapper) locked
        // on the icon.
        width: $section-title-icon-size;
        margin-left: $header-logo-size + $header-logo-spacing -
          $section-title-icon-size - $tab;
        margin-right: $tab;
        justify-content: center;
        align-items: center;
        position: relative;
        cursor: crosshair;
        .icon {
          height: $section-title-icon-size;
          width: $section-title-icon-size;
        }

        svg {
          transition:
            transform 0.25s ease,
            filter 0.25s ease;

          path {
            transition: fill 0.25s ease;
          }
        }

        // Target-lock reticle: four gold corner brackets, hidden at rest.
        &::before {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          width: 4.6rem;
          height: 4.6rem;
          transform: translate(-50%, -50%) scale(0.6);
          opacity: 0;
          pointer-events: none;
          filter: drop-shadow(0 0 0.4rem $gold-semi-transparent);
          background:
            linear-gradient($gold, $gold) 0 0 / 1rem 0.15rem,
            linear-gradient($gold, $gold) 0 0 / 0.15rem 1rem,
            linear-gradient($gold, $gold) 100% 0 / 1rem 0.15rem,
            linear-gradient($gold, $gold) 100% 0 / 0.15rem 1rem,
            linear-gradient($gold, $gold) 0 100% / 1rem 0.15rem,
            linear-gradient($gold, $gold) 0 100% / 0.15rem 1rem,
            linear-gradient($gold, $gold) 100% 100% / 1rem 0.15rem,
            linear-gradient($gold, $gold) 100% 100% / 0.15rem 1rem;
          background-repeat: no-repeat;
          transition:
            transform 0.22s cubic-bezier(0.2, 0.9, 0.3, 1.4),
            opacity 0.15s ease;
        }

        // Scanline: sweeps the reticle while locked on.
        &::after {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          width: 4.6rem;
          height: 0.15rem;
          transform: translate(-50%, calc(var(--reach) * -1));
          opacity: 0;
          pointer-events: none;
          background: linear-gradient(
            to right,
            transparent,
            $gold-semi-transparent 30%,
            $gold 50%,
            $gold-semi-transparent 70%,
            transparent
          );
        }

        &:hover {
          &::before {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }

          &::after {
            animation: icon-scan 1.4s linear infinite;
          }

          svg {
            transform: scale(1.1);
            filter: drop-shadow(0 0 0.6rem $gold-semi-transparent)
              drop-shadow(0 0 1.6rem $gold-semi-transparent);
            animation: holo-flicker 0.45s linear;

            path {
              fill: $gold-light;
            }
          }
        }
      }
    }
  }

  .content {
    @include technoCard();
    @include cardProse();
    // New formatting context so the card ring hugs floated side panels
    // instead of letting copy's floats spill past the card edge.
    display: flow-root;
    margin-left: $header-logo-size + $header-logo-spacing;
    margin-right: $header-logo-size + $header-logo-spacing;

    // ── Card architectures ─────────────────────────────────────────────
    // Composed onto this very element by information.component.tsx. Each is a
    // different ANSWER to one question — where does the art sit relative to
    // the copy — so no two sections on a page read as one layout flipped.
    // What they share: the art is always seated against a card edge and the
    // copy always meets it, so no treatment can strand art in empty card.

    // Split plate: the card's whole left flank is art, the copy sits in a
    // recessed console beside it. Short sections only (see cardSplitPlate).
    &.content-plate {
      @include cardSplitPlate(51%);

      // aspect-ratio here is a FLOOR, not a shape: it gives the grid a row
      // height to start from, and a taller console stretches the wall past it
      // — the art covers to fill, so the column can never letterbox.
      .frame {
        float: none;
        align-self: stretch;
        aspect-ratio: 4 / 3;
        width: calc(100% + 3.2rem);
        margin: -2.6rem 0 -2.6rem -3.2rem;
      }

      .media {
        height: 100%;
        aspect-ratio: auto;

        // The wall bleeds top AND bottom, so the cut keeps one arm: its right
        // side is the only edge left facing copy.
        .holo::before {
          display: none;
        }
      }

      .console {
        @include hudPanel();
      }
    }

    // Masthead banner: the section header lifts off the page and rides ON a
    // full-bleed art strip, which then dissolves into the copy beneath it.
    &.content-banner {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      row-gap: $tab;

      // The one place art meets the card edge: the ring paints OVER the strip
      // (the technoCard stroke sits at z -1 behind children everywhere else),
      // so the border is never swallowed by the art.
      &::before {
        z-index: 2;
      }

      // Strip and masthead share the first row; copy flows into the rest.
      .frame,
      .masthead {
        grid-area: 1 / 1;
      }

      .frame {
        margin-top: -2.6rem;
        // The bottom of the strip is masked to transparent (the dissolve
        // below), so the box's last stretch is invisible dead air. The
        // negative bottom margin hands that band back to the copy, which
        // starts inside the dissolve instead of below an empty zone.
        // Percentage = width-relative, so it scales with the 4:3 box at
        // every viewport; the 3.2rem eases the copy off the visible
        // floorboards (desktop only — mobile overrides these margins).
        margin-bottom: calc(-15% + 3.2rem);
        // Pure backdrop: the overflowing masked band must never eat clicks
        // or text selection from the copy sitting on top of it.
        pointer-events: none;

        .media {
          // Native ratio: the full image, never a cropped strip.
          aspect-ratio: 4 / 3;
          // The strip does not stop at a line. Its lower edge is masked away,
          // so the art dissolves into the card's own glass and the copy below
          // shares one surface with it instead of sitting under a seam.
          -webkit-mask-image: linear-gradient(
            to bottom,
            #000 0 74%,
            transparent 100%
          );
          mask-image: linear-gradient(to bottom, #000 0 74%, transparent 100%);
        }

        // A dissolve and a cut-line make opposite claims about one edge.
        .media::after {
          display: none;
        }
      }

      // The title sits in the strip's own dead air: this source carries a band
      // of empty attic across its top, and the masthead is what fills it.
      .masthead {
        position: relative;
        z-index: 1;
        align-self: start;
        margin: -2.6rem -3.2rem 0;
        padding: 3.6rem 3.2rem 8rem;
        background: linear-gradient(
          to bottom,
          rgba($main-darkest, 0.94) 0%,
          rgba($main-darkest, 0.78) 40%,
          rgba($main-darkest, 0) 100%
        );

        // On the art the gutter no longer has a page edge to align to: the
        // whole plate hangs off the strip's own left inset instead.
        .kicker,
        .icon-wrapper {
          margin-left: 0;
        }

        .header {
          margin-bottom: 0;
        }
      }
    }

    // Poster: the art IS the card. The 4:3 backdrop fills the whole box (same
    // ratio, so nothing crops) and the ring paints over it. Classic poster
    // composition: the statement opens the card at the top, the middle
    // belongs to the art alone, and the invitation + CTA close it together
    // at the bottom, grounded by a soft scrim over the bridge glow.
    &.content-poster {
      position: relative;
      display: flex;
      flex-direction: column;
      aspect-ratio: 4 / 3;

      // The ring must win against the backdrop, same as the banner strip.
      &::before {
        z-index: 2;
      }

      .frame {
        position: absolute;
        inset: 0;
        margin: 0;
        // Pure backdrop: never eats clicks from the copy and CTA on top.
        pointer-events: none;

        .media {
          height: 100%;
          aspect-ratio: auto;
        }
      }

      // The bled panel's replacement bottom rule would double the ring line
      // it sits on.
      .holo::before {
        display: none;
      }

      // Bottom scrim: grounds the closing copy against the bridge's glare
      // without dimming the gate. Sits between backdrop and copy.
      &::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 30%;
        z-index: 0;
        pointer-events: none;
        background: linear-gradient(
          to top,
          rgba($main-darkest, 0.82),
          rgba($main-darkest, 0.35) 55%,
          transparent
        );
      }

      // The copy sits in a generous poster frame: a big inset from the ring
      // on all sides, so statement and invitation float well inside the
      // canvas — over the art where they land, that's the poster's nature.
      // Only the CTA is exempt: it stays down on the bridge at the card's
      // bottom edge, full presence.
      > div {
        position: relative;
        z-index: 1;
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        padding: 4.4rem 4.4rem 0;

        blockquote {
          margin-bottom: 0;
        }

        // The invitation holds the right flank at the horizon line; the auto
        // margins center it in whatever height the card has left.
        p {
          align-self: flex-end;
          max-width: 46%;
          text-align: right;
          margin-top: auto;
          margin-bottom: auto;
        }

        // The closing CTA is the card's main feature: full walkway presence,
        // close to the bottom ring, outside the copy frame's big inset.
        > button:last-child {
          align-self: center;
          width: 70%;
          margin-top: 2.2rem;
          margin-bottom: 0;
        }
      }
    }

    p,
    ul {
      font-size: $card-text-font-size;
      margin-bottom: 0;
    }

    // Rhythm only. The steel hairline that used to rule off every paragraph
    // competed with the card's own ring for attention, and stacked four of
    // them into a section that reads as one thought.
    p + p {
      margin-top: $tab;
    }

    // Scoped to the card rather than to <p>: leads and list items carry links
    // too, and they should all read the same.
    a {
      color: $url;
      text-decoration: none;
      &:hover {
        color: $url-hoover;
        text-shadow: $text-glow-strong;
      }
      &:active {
        color: $url-hoover;
        text-shadow: $text-glow-strong;
      }
      &:visited {
        color: $url;
      }
    }
  }

  @media all and (max-width: $mobile-large) {
    margin-top: 8rem;
    margin-bottom: $tab-quadruple-mobile-large;
    .kicker {
      margin-left: $header-logo-size-mobile-large +
        $header-logo-spacing-mobile-large;
      margin-bottom: 0.8rem;
      font-size: $kicker-font-size-mobile-large;
    }

    .column {
      .header {
        margin-bottom: $tab-mobile-large;
        h2 {
          font-size: $section-title-font-size-mobile-large;
        }
        .icon-wrapper {
          --reach: 1.8rem;
          width: $section-title-icon-size-mobile-large;
          margin-left: $header-logo-size-mobile-large +
            $header-logo-spacing-mobile-large -
            $section-title-icon-size-mobile-large - $tab-mobile-large;
          margin-right: $tab-mobile-large;
          .icon {
            height: $section-title-icon-size-mobile-large;
            width: $section-title-icon-size-mobile-large;
          }

          &::before,
          &::after {
            width: 3.6rem;
          }

          &::before {
            height: 3.6rem;
          }
        }
      }
    }

    .content {
      @include technoCardMobile();
      margin-left: 0;
      margin-right: 0;

      // Every architecture collapses to the same stack on a phone: art first,
      // edge to edge, native ratio, copy under it. The sideways bleed stays —
      // full-width art is the point — and the top bleed stays too, because in
      // all of them the panel is the card's first child.
      &.content-plate,
      &.content-banner,
      &.content-poster {
        .frame {
          align-self: auto;
          aspect-ratio: auto;
          width: calc(100% + 4rem);
          margin: -1.8rem -2rem $tab-mobile-large;
        }

        .media {
          height: auto;
          aspect-ratio: auto;
        }
      }

      // The poster un-posters on a phone: a 4:3 overlay leaves no room for
      // copy at phone widths, so it becomes the standard stacked card.
      &.content-poster {
        display: block;
        aspect-ratio: auto;

        .frame {
          position: static;
          pointer-events: auto;
        }

        > div {
          display: block;
        }
      }

      &.content-banner {
        row-gap: $tab-mobile-large;

        .frame {
          margin-bottom: 0;
        }

        .masthead {
          margin: -1.8rem -2rem 0;
          padding: 2.4rem 2rem 6rem;
        }
      }

      p,
      ul {
        font-size: $card-text-font-size-mobile-large;
      }

      blockquote {
        padding-left: $tab-mobile-large;
        font-size: $card-lead-font-size-mobile-large;
        margin-bottom: $tab-double-mobile-large;
      }

      p + blockquote,
      ul + blockquote {
        margin-top: $tab-double-mobile-large;
      }
    }
  }

  @media all and (max-width: $mobile-small) {
    margin-top: 6rem;
    margin-bottom: $tab-quadruple-mobile-small;
    .kicker {
      // Both tokens are vw here, so they add in Sass directly — unlike
      // .icon-wrapper below, which needs calc() to mix in a rem tab.
      margin-left: $header-logo-size-mobile-small +
        $header-logo-spacing-mobile-small;
      font-size: $kicker-font-size-mobile-small;
    }

    .column {
      .header {
        margin-bottom: $tab-mobile-small;
        h2 {
          font-size: $section-title-font-size-mobile-small;
        }
        .icon-wrapper {
          width: $section-title-icon-size-mobile-small;
          // vw gutter minus the rem gap needs calc: units cannot mix in Sass.
          margin-left: calc(
            #{$header-logo-size-mobile-small + $header-logo-spacing-mobile-small -
              $section-title-icon-size-mobile-small} - #{$tab-mobile-small}
          );
          margin-right: $tab-mobile-small;
          .icon {
            height: $section-title-icon-size-mobile-small;
            width: $section-title-icon-size-mobile-small;
          }

          // Icons are viewport-scaled and tiny here; drop the hover chrome.
          &::before,
          &::after {
            display: none;
          }
        }
      }
    }

    .content {
      p,
      ul {
        font-size: $card-text-font-size-mobile-small;
      }

      &.content-plate .frame,
      &.content-banner .frame {
        margin-bottom: $tab-mobile-small;
      }

      &.content-banner {
        row-gap: $tab-mobile-small;

        .frame {
          margin-bottom: 0;
        }
      }

      blockquote {
        padding-left: $tab-mobile-small;
        font-size: $card-lead-font-size-mobile-small;
        margin-bottom: $tab-double-mobile-small;
      }

      p + blockquote,
      ul + blockquote {
        margin-top: $tab-double-mobile-small;
      }
    }
  }
}

// ─── Holo-viewport media panel ───────────────────────────────────────────
// Standalone top-level rules: the panel renders inside section cards AND on
// its own (contact deck), so nothing here may depend on an .information
// ancestor.
//
// Two boxes on purpose. .frame is the LAYOUT shell — float, width, bleed
// margins, caption bar — and .media is the frame proper, which clips its own
// contents to a chamfered octagon. Keeping them apart is what lets a chassis
// caption hang off a panel instead of being clipped away by it.
.frame {
  // Floated panels resolve their width from these two: a shape accent sets the
  // share of the card, a bleed adds back the card padding the panel eats.
  --m-w: 58%;
  --m-bleed: 0rem;

  display: block;
  margin: 0;
}

// Chamfered octagon glass with a steel double-loop ring; instead of repeating
// the cards' gold tick clusters, it wears a gold-dipped top-right chamfer (the
// frame's fin-dip vocabulary). Hover warms the ring and runs a scan sweep.
.media {
  --if-cut: 1.6rem;
  --if-line: 0.15rem;
  --if-i: max(0px, calc(var(--if-cut) - 0.414 * var(--if-line)));

  position: relative;
  z-index: 0;
  clip-path: polygon(
    var(--if-cut) 0,
    calc(100% - var(--if-cut)) 0,
    100% var(--if-cut),
    100% calc(100% - var(--if-cut)),
    calc(100% - var(--if-cut)) 100%,
    var(--if-cut) 100%,
    0 calc(100% - var(--if-cut)),
    0 var(--if-cut)
  );

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    background: linear-gradient(to left, $main-lightish, $main-light);
    transition: background 0.25s ease;
    clip-path: polygon(
      var(--if-cut) 0,
      calc(100% - var(--if-cut)) 0,
      100% var(--if-cut),
      100% calc(100% - var(--if-cut)),
      calc(100% - var(--if-cut)) 100%,
      var(--if-cut) 100%,
      0 calc(100% - var(--if-cut)),
      0 var(--if-cut),
      var(--if-cut) 0,
      calc(var(--if-line) + var(--if-i)) var(--if-line),
      var(--if-line) calc(var(--if-line) + var(--if-i)),
      var(--if-line) calc(100% - var(--if-line) - var(--if-i)),
      calc(var(--if-line) + var(--if-i)) calc(100% - var(--if-line)),
      calc(100% - var(--if-line) - var(--if-i)) calc(100% - var(--if-line)),
      calc(100% - var(--if-line)) calc(100% - var(--if-line) - var(--if-i)),
      calc(100% - var(--if-line)) calc(var(--if-line) + var(--if-i)),
      calc(100% - var(--if-line) - var(--if-i)) var(--if-line),
      calc(var(--if-line) + var(--if-i)) var(--if-line)
    );
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    // Focal point, not a crop preference: the layouts that ask a 4:3 source to
    // hold a strip or a portrait set --media-focus to the region that carries
    // the subject. Everything else is uncropped and centres.
    object-position: var(--media-focus, center);
    // Native color at rest: the art is never dimmed; hover still pops it
    // (saturate/brightness 1.05) alongside the ring warm-up and scan.
    transition: filter 0.25s ease;
  }

  .holo {
    position: absolute;
    inset: 0;
    pointer-events: none;
    // Faint horizontal scanlines for the holo feel.
    background: repeating-linear-gradient(
      0deg,
      transparent 0,
      transparent 0.35rem,
      rgba($main-darkest, 0.12) 0.35rem,
      rgba($main-darkest, 0.12) 0.4rem
    );

    // Gold-dipped chamfer: a metallic band hugging the top-right corner
    // cut, just under the steel ring. The gradient runs along the band,
    // bright at its upper end and dissolving past its lower tip.
    &::before {
      content: "";
      position: absolute;
      inset: 0;
      clip-path: polygon(
        calc(100% - 2.6rem) 0,
        calc(100% - 1.6rem) 0,
        100% 1.6rem,
        100% 2.6rem
      );
      background: linear-gradient(
        135deg,
        $gold-light 0%,
        $gold 35%,
        $gold-dark 75%,
        rgba($gold-dark, 0) 102%
      );
    }

    // Scan sweep bar, hidden at rest.
    &::after {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      height: 0.15rem;
      opacity: 0;
      background: linear-gradient(
        to right,
        transparent,
        $gold-semi-transparent 30%,
        $gold 50%,
        $gold-semi-transparent 70%,
        transparent
      );
    }
  }

  &:hover {
    &::before {
      background: linear-gradient(to left, $gold-dark, $gold);
    }

    img {
      filter: saturate(1.05) brightness(1.05);
    }

    .holo::after {
      animation: media-scan 1.6s linear infinite;
    }
  }
}

// Banner: a full-width plate across the copy at the source's native ratio.
.media-top {
  width: calc(100% + var(--m-bleed) * 2);
  margin-bottom: 2.2rem;

  .media {
    aspect-ratio: 4 / 3;
  }
}

// Free-standing panel: fills its container at the image's native ratio and
// never crops. Spacing is the consumer's business.
.media-fill {
  width: 100%;

  .media {
    aspect-ratio: auto;
  }

  img {
    height: auto;
  }
}

// Magazine floats: the panel sits in the copy and the copy wraps under it.
// Every source image is a native 4:3 and every panel keeps that ratio — width
// is the only per-section variable (default / wide).
.media-left {
  float: left;
  width: calc(var(--m-w) + var(--m-bleed));
  margin: 0.4rem 2.6rem 1.6rem 0;

  .media {
    aspect-ratio: 4 / 3;
  }
}

.media-right {
  float: right;
  width: calc(var(--m-w) + var(--m-bleed));
  margin: 0.4rem 0 1.6rem 2.6rem;

  .media {
    aspect-ratio: 4 / 3;
  }
}

// Scale accent composed onto a floated panel.
.media-wide {
  --m-w: 62%;
}

// Closing panel: full width under the copy and CTA, native ratio, framed,
// inside the ring — the card's final beat.
.media-bottom {
  clear: both;
  width: 100%;
  margin: 2.6rem 0 0;

  .media {
    aspect-ratio: auto;
  }

  img {
    height: auto;
  }
}

// ─── Edge bleed ──────────────────────────────────────────────────────────
// Reserved for TOTAL interruptions only — the banner masthead and the plate
// wall, whose art spans its entire flank so the card ring closes cleanly
// against it. A partial cut (a floated panel notching the ring mid-run) reads
// as a broken border, so floated and bottom panels never take this class.
// The panel drops its own frame — two rings doubling up on one line visibly
// STOPPING at the art — plus a gold rule closing the edge that faces copy.
.media-bleed {
  --m-bleed: 3.2rem;

  .media {
    --if-cut: 0rem;

    &::before {
      display: none;
    }

    &::after {
      content: "";
      position: absolute;
      z-index: 1;
      pointer-events: none;
    }
  }

  // The gold chamfer dip has lost the chamfer it was dipped into, so it takes
  // over as the cut's second arm: with ::after down the inner side, the two
  // meet at the corner and close the panel where it still meets copy.
  .holo::before {
    inset: auto 0 0 0;
    height: 0.2rem;
    clip-path: none;
  }
}

// A banner bleeds both sides, so only the bottom faces copy: one rule, and no
// second arm to close it against.
.media-bleed.media-top {
  margin-left: -3.2rem;

  .media::after {
    left: 0;
    right: 0;
    bottom: 0;
    height: 0.2rem;
    background: linear-gradient(
      to right,
      $gold-transparent,
      $gold 18%,
      $gold 82%,
      $gold-transparent
    );
  }

  .holo::before {
    display: none;
  }
}

// ─── Chassis caption ─────────────────────────────────────────────────────
// A readout bolted under the panel: the cards' gold slash ticks, a display-face
// label, then a hairline running out to the panel's edge. An instrument label,
// not a photo credit — so it is set in the identity voice and stays tiny.
.caption {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  margin-top: 1rem;
  font-family: $font-display-bold;
  font-size: $kicker-font-size;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: $gold;

  &::before {
    content: "";
    flex: none;
    width: 2.6rem;
    height: 0.7rem;
    background: repeating-linear-gradient(
      -45deg,
      $gold-semi-transparent 0,
      $gold-semi-transparent 0.2rem,
      transparent 0.2rem,
      transparent 0.8rem
    );
  }

  &::after {
    content: "";
    flex: 1;
    height: 0.1rem;
    background: linear-gradient(to right, $gold-semi-transparent, $gold-transparent);
  }
}

// A bled panel runs out to the card's edge; its label must not.
.media-bleed .caption {
  padding: 0 3.2rem;
}

// Mid-flow banner: sitting between paragraphs it needs its own top separation,
// since it breaks the p + p adjacency that carries the paragraph rhythm gap.
p + .media-top {
  margin-top: 2.2rem;
}

// Full width at the image's native ratio below tablet width: nothing is
// ever cropped on mobile, whatever shape the source has.
@media all and (max-width: $mobile-large) {
  .media-top,
  .media-bottom,
  .media-left,
  .media-right {
    float: none;
    width: 100%;
    margin: 0 0 $tab-mobile-large;

    .media {
      aspect-ratio: auto;
    }

    img {
      height: auto;
    }
  }

  // Bleeds survive the stack — edge-to-edge art is the whole point — but on
  // the card's mobile padding, and sideways only: a phone card is one column,
  // so a panel mid-column has no card edge above it to lock into.
  .media-bleed.media-top,
  .media-bleed.media-left,
  .media-bleed.media-right {
    width: calc(100% + 4rem);
    margin-top: 0;
    margin-left: -2rem;
    margin-right: -2rem;

    // One cut-line along the bottom for all of them: stacked full width, the
    // edge that still faces copy is never a side.
    .media::after {
      top: auto;
      left: 0;
      right: 0;
      bottom: 0;
      width: auto;
      height: 0.2rem;
      background: linear-gradient(
        to right,
        $gold-transparent,
        $gold 18%,
        $gold 82%,
        $gold-transparent
      );
    }

    .holo::before {
      display: none;
    }
  }

  .media-bleed .caption {
    padding: 0 2rem;
  }

  p + .media-top {
    margin-top: $tab-mobile-large;
  }
}

@media all and (max-width: $mobile-small) {
  .media-top,
  .media-bottom,
  .media-left,
  .media-right {
    margin-bottom: $tab-mobile-small;
  }

  .caption {
    gap: 0.8rem;
    font-size: $kicker-font-size-mobile-small;
  }

  p + .media-top {
    margin-top: $tab-mobile-small;
  }
}

@keyframes icon-scan {
  0% {
    transform: translate(-50%, calc(var(--reach) * -1));
    opacity: 0;
  }
  15% {
    opacity: 1;
  }
  85% {
    opacity: 1;
  }
  100% {
    transform: translate(-50%, var(--reach));
    opacity: 0;
  }
}

@keyframes holo-flicker {
  0%,
  100% {
    opacity: 1;
  }
  20% {
    opacity: 0.4;
  }
  35% {
    opacity: 0.9;
  }
  50% {
    opacity: 0.55;
  }
  65% {
    opacity: 1;
  }
}

@keyframes media-scan {
  0% {
    top: 0;
    opacity: 0;
  }
  15% {
    opacity: 1;
  }
  85% {
    opacity: 1;
  }
  100% {
    top: calc(100% - 0.15rem);
    opacity: 0;
  }
}
